import { Page } from "../components/layout/Page";
import { useLoginContext } from "../context/LoginContext";
import {
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { VerticalStack } from "../components/primitives/Stacks";
import { useTranslation } from "react-i18next";

export const LoginPage: React.FC = () => {
  const [mode, setMode] = useState<"login" | "create">("login");

  return (
    <Page>
      {mode === "login" ? (
        <LoginView toggleCreate={() => setMode("create")} />
      ) : (
        <CreateUserView toggleLogin={() => setMode("login")} />
      )}
    </Page>
  );
};

const LoginView: React.FC<{ toggleCreate: () => void }> = ({
  toggleCreate,
}) => {
  const { t } = useTranslation();
  const { login } = useLoginContext();

  const [{ username, password, error }, setData] = useState<{
    username: string;
    password: string;
    error?: string;
  }>({ username: "", password: "" });

  const doLogin = useCallback(
    (data: { userId: string; password: string }) => {
      setData((d) => ({ ...d, error: undefined }));
      login(data).then((success) => {
        if (!success)
          setData((d) => ({ ...d, error: "User unknown or wrong password" }));
      });
    },
    [login]
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      doLogin({ userId: username, password });
    },
    [doLogin, password, username]
  );

  return (
    <Fade in appear>
      <Container disableGutters maxWidth="xs" sx={{ py: 4 }}>
        <Typography
          align="center"
          color="text.secondary"
          display="block"
          gutterBottom
          variant="overline"
        >
          {t("auth.signin-overline")}
        </Typography>
        <Typography align="center" variant="h2" sx={{ pb: 4 }}>
          {t("auth.signin-title")}
        </Typography>
        <VerticalStack>
          <Card>
            <CardContent
              component="form"
              onSubmit={onSubmit}
              sx={{ "&, &:last-child": { p: { xs: 3, md: 4, xl: 5 } } }}
            >
              <TextField
                autoComplete="name"
                error={!!error}
                required
                label={t("auth.username-label")}
                margin="dense"
                value={username}
                onChange={(e) =>
                  setData((d) => ({
                    ...d,
                    username: e.target.value || "",
                    error: undefined,
                  }))
                }
              />
              <TextField
                autoComplete="password"
                error={!!error}
                label={t("auth.password-label")}
                margin="dense"
                required
                type="password"
                value={password}
                onChange={(e) =>
                  setData((d) => ({
                    ...d,
                    password: e.target.value || "",
                    error: undefined,
                  }))
                }
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
              <Button
                disabled={!username || !password}
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
              >
                {t("auth.signin-cta")}
              </Button>
            </CardContent>
          </Card>
          <Button fullWidth onClick={toggleCreate} variant="text">
            {t("auth.signup-prompt")}
          </Button>
          <Typography color="text.disabled" display="block" variant="caption">
            {t("auth.credentials-footnote")}
          </Typography>
        </VerticalStack>
      </Container>
    </Fade>
  );
};

const CreateUserView: React.FC<{ toggleLogin: () => void }> = ({
  toggleLogin,
}) => {
  const { t } = useTranslation();

  const [{ username, password, name }, setData] = useState<{
    username: string;
    password: string;
    name: string;
  }>({ username: "", password: "", name: "" });

  const { createNewUser } = useLoginContext();

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      createNewUser({ userId: username, password, name });
    },
    [createNewUser, password, username, name]
  );

  return (
    <Fade in appear>
      <Container disableGutters maxWidth="xs" sx={{ py: 4 }}>
        <Typography
          align="center"
          color="text.secondary"
          display="block"
          gutterBottom
          variant="overline"
        >
          {t("auth.signup-overline")}
        </Typography>
        <Typography align="center" variant="h2" sx={{ pb: 4 }}>
          {t("auth.signup-title")}
        </Typography>
        <VerticalStack>
          <Card>
            <CardContent
              component="form"
              onSubmit={onSubmit}
              sx={{ "&, &:last-child": { p: { xs: 3, md: 4, xl: 5 } } }}
            >
              <TextField
                autoComplete="name"
                required
                label={t("auth.name-label")}
                margin="dense"
                value={name}
                onChange={(e) =>
                  setData((d) => ({ ...d, name: e.target.value || "" }))
                }
              />
              <TextField
                autoComplete="username"
                required
                label={t("auth.username-label")}
                value={username}
                margin="dense"
                onChange={(e) =>
                  setData((d) => ({ ...d, username: e.target.value || "" }))
                }
              />
              <TextField
                autoComplete="new-password"
                label={t("auth.password-label")}
                margin="dense"
                required
                type="password"
                value={password}
                onChange={(e) =>
                  setData((d) => ({ ...d, password: e.target.value || "" }))
                }
              />
              <Button
                disabled={!username || !password || !name}
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
              >
                {t("auth.signup-cta")}
              </Button>
            </CardContent>
          </Card>
          <Button fullWidth onClick={toggleLogin} variant="text">
            {t("auth.signin-prompt")}
          </Button>
          <Typography color="text.disabled" display="block" variant="caption">
            {t("auth.credentials-footnote")}
          </Typography>
        </VerticalStack>
      </Container>
    </Fade>
  );
};
